import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { AlertTriangle, ChevronDown, SlidersHorizontal } from 'lucide-react';
import { API } from '../../App';

/* EngineSettingsList — Phase-2 declutter (2026-08-03): the live engine knobs
   as ROWS in one card instead of one hero card per toggle. Supersedes
   UsageAwarePanel + StatefulCapacityPanel (their logic absorbed verbatim —
   same GET, same POST fields, same optimistic-revert). One fetch instead of
   two; the long "why" copy lives in title= tooltips, not paragraphs.

   Contract (unchanged): GET .../node-optimization/rebalance-config reads
   engine_rebalancer.{usage_aware_mode, critical_on_demand}; POSTs write
   engine_usage_aware_mode / engine_critical_on_demand
   (backend -> agent -> RebalancerConfig CRD, live, no restart). */

const USAGE_MODES = [
  { value: 'off', label: 'Off' },
  { value: 'shadow', label: 'Preview only' },
  { value: 'enforce', label: 'Enabled' },
];
const CAPACITY_MODES = [
  { value: 'ondemand', label: 'On-demand (recommended)' },
  { value: 'spot', label: 'Spot' },
];

const Row = ({ title, hint, tooltip, control, warning }) => (
  <div className="px-5 py-4">
    <div className="flex items-center justify-between gap-4">
      <div title={tooltip}>
        <div className="text-sm font-medium text-slate-900 dark:text-slate-100">{title}</div>
        <div className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">{hint}</div>
      </div>
      <div className="relative shrink-0">{control}</div>
    </div>
    {warning}
  </div>
);

const Select = ({ value, onChange, disabled, options }) => (
  <>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className="appearance-none border border-slate-300 rounded-lg px-3 py-1.5 pr-8 text-sm font-medium text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"
    >
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
    <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
  </>
);

export default function EngineSettingsList({ clusterId }) {
  const [usageMode, setUsageMode] = useState('off');
  const [capacity, setCapacity] = useState('spot'); // engine base is all-spot until set
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!clusterId) return;
    try {
      const r = await axios.get(`${API}/v1/clusters/${clusterId}/node-optimization/rebalance-config`);
      setUsageMode(r.data?.engine_rebalancer?.usage_aware_mode || 'off');
      setCapacity(r.data?.engine_rebalancer?.critical_on_demand === true ? 'ondemand' : 'spot');
    } catch {
      /* keep defaults */
    }
  }, [clusterId]);

  useEffect(() => { load(); }, [load]);

  const save = async (body, apply, revert) => {
    apply();
    setSaving(true);
    try {
      await axios.post(`${API}/v1/clusters/${clusterId}/node-optimization/rebalance-config`, body);
    } catch {
      revert();
    } finally {
      setSaving(false);
    }
  };

  if (!clusterId) return null;

  return (
    <div className="rounded-xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
      <div className="flex items-center gap-2 border-b border-slate-100 dark:border-slate-800 px-5 py-3">
        <SlidersHorizontal className="h-4 w-4 text-slate-400" />
        <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Engine settings</h3>
        <span className="text-xs text-slate-400">applied live — no restart</span>
      </div>
      <div className="divide-y divide-slate-100 dark:divide-slate-800">
        <Row
          title="Usage-aware consolidation"
          hint="Pack nodes on actual usage, not requests."
          tooltip="Packs nodes on live usage instead of pod requests, reclaiming idle capacity that request-based Karpenter leaves running (RebalancerConfig usageAwareMode). Preview logs what would consolidate without acting."
          control={
            <Select
              value={usageMode}
              disabled={saving}
              options={USAGE_MODES}
              onChange={(v) => {
                const prev = usageMode;
                save({ engine_usage_aware_mode: v }, () => setUsageMode(v), () => setUsageMode(prev));
              }}
            />
          }
          warning={usageMode === 'enforce' && (
            <div className="mt-2 flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-500">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
              <span>Consolidating on live usage — run Preview first to review the impact.</span>
            </div>
          )}
        />
        <Row
          title="Stateful workload capacity"
          hint="Where databases and other stateful workloads run."
          tooltip="On-demand keeps stateful workloads (StatefulSet, do-not-disrupt, PVC-backed) off spot so their state survives a reclaim (RebalancerConfig criticalOnDemand). Per-workload override: annotate optops.io/spot-friendly=true to run one on spot, =false to pin one to on-demand."
          control={
            <Select
              value={capacity}
              disabled={saving}
              options={CAPACITY_MODES}
              onChange={(v) => {
                const prev = capacity;
                save({ engine_critical_on_demand: v === 'ondemand' }, () => setCapacity(v), () => setCapacity(prev));
              }}
            />
          }
        />
      </div>
    </div>
  );
}
